import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Stack,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAccountQuery } from "../../../features/auth/authApi";

interface TradingFormValues {
  tradedDigitalAssets: boolean;
  tradedFutures: boolean;
  tradedFX: boolean;
  tradedEquities: boolean;
  tradedOptions: boolean;
  tradedNone: boolean;
  otherTradingExperience: string;
  estimatedTradeFrequency: string;

  estimatedCryptoValue: string;

  estimatedTurnover: string;

  hasLicensesIssues: string;
  subjectToEnforcement: string;
  subjectToLitigation: string;
  tradingOptionGroup?: boolean;
}

const initialValues: TradingFormValues = {
  tradedDigitalAssets: false,
  tradedFutures: false,
  tradedFX: false,
  tradedEquities: false,
  tradedOptions: false,
  tradedNone: false,
  otherTradingExperience: "",
  estimatedTradeFrequency: "",
  estimatedCryptoValue: "",
  estimatedTurnover: "",
  hasLicensesIssues: "",
  subjectToEnforcement: "",
  subjectToLitigation: "",
  tradingOptionGroup: false,
};

const validationSchema = Yup.object().shape({
  tradedDigitalAssets: Yup.boolean(),
  tradedFutures: Yup.boolean(),
  tradedFX: Yup.boolean(),
  tradedEquities: Yup.boolean(),
  tradedOptions: Yup.boolean(),
  tradedNone: Yup.boolean(),
  otherTradingExperience: Yup.string(),

  estimatedTradeFrequency: Yup.string().required("Select trade frequency"),
  estimatedCryptoValue: Yup.string().required("Select estimated crypto value"),
  estimatedTurnover: Yup.string().required("Select estimated turnover"),

  hasLicensesIssues: Yup.string().required("Please select Yes or No"),
  subjectToEnforcement: Yup.string().required("Please select Yes or No"),
  subjectToLitigation: Yup.string().required("Please select Yes or No"),
  tradingOptionGroup: Yup.boolean().test(
    "at-least-one-trade",
    "Please select at least one trading option",
    function () {
      const {
        tradedDigitalAssets,
        tradedFutures,
        tradedFX,
        tradedEquities,
        tradedOptions,
        tradedNone,
      } = this.parent;
      return Boolean(
        tradedDigitalAssets ||
          tradedFutures ||
          tradedFX ||
          tradedEquities ||
          tradedOptions ||
          tradedNone
      );
    }
  ),
});

const CompanyInformationForm5 = () => {
  const navigate = useNavigate();
  const { data: account } = useAccountQuery();

  const onSubmit = (values: TradingFormValues) => {
    const storedData = JSON.parse(localStorage.getItem("form3_json") || "{}");
    const mergedData = { ...storedData, ...values };
    localStorage.setItem("form3_json", JSON.stringify(mergedData));

    navigate("/separate-corp-upload-docs");
  };

  const acctJson = account?.user.form3_json;
  let parsedFromAccount: Partial<TradingFormValues> = {};
  if (acctJson) {
    try {
      parsedFromAccount =
        typeof acctJson === "object" ? acctJson : JSON.parse(acctJson);
    } catch {
      console.warn("Cant parse form3_json");
    }
  }

  const filteredParsed: Partial<TradingFormValues> = {};
  (Object.keys(initialValues) as Array<keyof TradingFormValues>).forEach(
    (key) => {
      if (parsedFromAccount[key] !== undefined) {
        filteredParsed[key] = parsedFromAccount[key] as any;
      }
    }
  );

  const combinedInitialValues: TradingFormValues = {
    ...initialValues,
    ...filteredParsed,
  };

  const {
    handleSubmit,
    values,
    errors,
    touched,
    setFieldValue,
    getFieldProps,
    isSubmitting,
  } = useFormik<TradingFormValues>({
    initialValues: combinedInitialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const errorText = (fieldName: keyof TradingFormValues) =>
    touched[fieldName] && errors[fieldName]
      ? (errors[fieldName] as string)
      : "";

  return (
    <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
      <Container maxWidth="md">
        <form onSubmit={handleSubmit}>
          <Card
            elevation={24}
            component={motion.div}
            layoutId="trading-experience-card"
          >
            <CardContent>
              <Stack spacing={4}>
                <Typography variant="h5" fontWeight="600">
                  Trading Experience
                </Typography>

                <Typography>
                  Which of the following has the entity traded in the past 12
                  months?
                </Typography>
                <Grid container spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.tradedDigitalAssets}
                        disabled={values.tradedNone}
                        onChange={(e) => {
                          setFieldValue(
                            "tradedDigitalAssets",
                            e.target.checked
                          );
                        }}
                      />
                    }
                    label="Digital Assets"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={values.tradedNone}
                        checked={values.tradedFutures}
                        onChange={(e) =>
                          setFieldValue("tradedFutures", e.target.checked)
                        }
                      />
                    }
                    label="Futures"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={values.tradedNone}
                        checked={values.tradedFX}
                        onChange={(e) =>
                          setFieldValue("tradedFX", e.target.checked)
                        }
                      />
                    }
                    label="FX"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={values.tradedNone}
                        checked={values.tradedEquities}
                        onChange={(e) =>
                          setFieldValue("tradedEquities", e.target.checked)
                        }
                      />
                    }
                    label="Equities"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={values.tradedNone}
                        checked={values.tradedOptions}
                        onChange={(e) =>
                          setFieldValue("tradedOptions", e.target.checked)
                        }
                      />
                    }
                    label="Options"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.tradedNone}
                        onChange={(e) => {
                          const isNoneChecked = e.target.checked;
                          setFieldValue("tradedNone", isNoneChecked);

                          if (isNoneChecked) {
                            setFieldValue("tradedDigitalAssets", false);
                            setFieldValue("tradedFutures", false);
                            setFieldValue("tradedFX", false);
                            setFieldValue("tradedEquities", false);
                            setFieldValue("tradedOptions", false);
                          }
                        }}
                      />
                    }
                    label="None of the above"
                  />
                </Grid>
                {typeof errors.tradingOptionGroup === "string" && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.tradingOptionGroup}
                  </Typography>
                )}
                <TextField
                  label="Any other relevant trading experience (if applicable)"
                  multiline
                  minRows={2}
                  {...getFieldProps("otherTradingExperience")}
                />

                <FormLabel sx={{ mt: 2 }}>
                  Estimated Trade Frequency (per month)
                </FormLabel>
                <RadioGroup row {...getFieldProps("estimatedTradeFrequency")}>
                  <FormControlLabel
                    value="1-5 Trades per month"
                    control={<Radio />}
                    label="1-5 Trades per month"
                  />
                  <FormControlLabel
                    value="5-10 Trades per month"
                    control={<Radio />}
                    label="5-10 Trades per month"
                  />
                  <FormControlLabel
                    value="10-30 Trades per month"
                    control={<Radio />}
                    label="10-30 Trades per month"
                  />
                  <FormControlLabel
                    value="30+ Trades per month"
                    control={<Radio />}
                    label="30+ Trades per month"
                  />
                </RadioGroup>
                {errorText("estimatedTradeFrequency") && (
                  <Typography variant="caption" color="error">
                    {errorText("estimatedTradeFrequency")}
                  </Typography>
                )}

                <Typography variant="h6" sx={{ mt: 2 }}>
                  Estimated value of cryptocurrency to be traded over the next
                  12 months
                </Typography>
                <RadioGroup row {...getFieldProps("estimatedCryptoValue")}>
                  <FormControlLabel
                    value="Under €1,000"
                    control={<Radio />}
                    label="Under €1,000"
                  />
                  <FormControlLabel
                    value="€1,000 to €10,000"
                    control={<Radio />}
                    label="€1,000 to €10,000"
                  />
                  <FormControlLabel
                    value="€10,000 to €100,000"
                    control={<Radio />}
                    label="€10,000 to €100,000"
                  />
                  <FormControlLabel
                    value="€100,000 to €1,000,000"
                    control={<Radio />}
                    label="€100,000 to €1,000,000"
                  />
                  <FormControlLabel
                    value="Above €1,000,000"
                    control={<Radio />}
                    label="Above €1,000,000"
                  />
                </RadioGroup>
                {errorText("estimatedCryptoValue") && (
                  <Typography variant="caption" color="error">
                    {errorText("estimatedCryptoValue")}
                  </Typography>
                )}

                <Typography variant="h6" sx={{ mt: 2 }}>
                  Account estimated turnover (per month)
                </Typography>
                <RadioGroup row {...getFieldProps("estimatedTurnover")}>
                  <FormControlLabel
                    value="€1,000 to €10,000"
                    control={<Radio />}
                    label="€1,000 to €10,000"
                  />
                  <FormControlLabel
                    value="€10,000 to €100,000"
                    control={<Radio />}
                    label="€10,000 to €100,000"
                  />
                  <FormControlLabel
                    value="€100,000 to €1,000,000"
                    control={<Radio />}
                    label="€100,000 to €1,000,000"
                  />
                  <FormControlLabel
                    value="Above €1,000,000"
                    control={<Radio />}
                    label="Above €1,000,000"
                  />
                </RadioGroup>
                {errorText("estimatedTurnover") && (
                  <Typography variant="caption" color="error">
                    {errorText("estimatedTurnover")}
                  </Typography>
                )}

                <Typography variant="h5" fontWeight="600" sx={{ mt: 3 }}>
                  Additional Information About Your Company
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                  Has the company, your subsidiaries, affiliates,
                  directors/shareholders/controllers/UBOs or any company
                  directly associated with you, its owners or its directors ever
                  been subject to or currently subject to any of the following:
                </Typography>

                <FormLabel sx={{ mt: 2 }}>
                  Licenses suspended, revoked or received notice of regulatory
                  investigation?
                </FormLabel>
                <RadioGroup row {...getFieldProps("hasLicensesIssues")}>
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {errorText("hasLicensesIssues") && (
                  <Typography variant="caption" color="error">
                    {errorText("hasLicensesIssues")}
                  </Typography>
                )}

                <FormLabel sx={{ mt: 2 }}>
                  Subject to enforcement action or CIG/Regulatory Sanctions?
                </FormLabel>
                <RadioGroup row {...getFieldProps("subjectToEnforcement")}>
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {errorText("subjectToEnforcement") && (
                  <Typography variant="caption" color="error">
                    {errorText("subjectToEnforcement")}
                  </Typography>
                )}

                <FormLabel sx={{ mt: 2 }}>
                  Subject to any ongoing litigation, legal or regulatory
                  violations or bankruptcy proceedings?
                </FormLabel>
                <RadioGroup row {...getFieldProps("subjectToLitigation")}>
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {errorText("subjectToLitigation") && (
                  <Typography variant="caption" color="error">
                    {errorText("subjectToLitigation")}
                  </Typography>
                )}

                <Stack spacing={2} mt={3}>
                  <LoadingButton
                    loading={isSubmitting}
                    type="submit"
                    variant="contained"
                  >
                    Save &amp; Continue
                  </LoadingButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default CompanyInformationForm5;
